import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal'; // Import Modal from React-Bootstrap
import { GoogleLogin } from '@react-oauth/google';
import { register } from '../utils/auth';
import { useAuthStore } from '../store/auth';
import useLogin, { useGoogleLogin } from '../utils/login';
import ButtonExample from "../components/loadingbutton.js";
import axios from '../utils/axios';

const REGISTRATION_STATUS_POLL_MS = 5000;

// Public sitekey — safe to ship in the JS bundle, same treatment as
// api_key in calculator.js. The private secret key lives server-side only
// (backend/settings.py TURNSTILE_SECRET_KEY), never in frontend code.
const TURNSTILE_SITE_KEY = '0x4AAAAAAEW6fClba5FEuuA0';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Loads Cloudflare's Turnstile script at most once, even if multiple
// RegisterForm instances mount (e.g. modal open/close/reopen).
function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
        return new Promise((resolve) => {
            const check = setInterval(() => {
                if (window.turnstile) {
                    clearInterval(check);
                    resolve();
                }
            }, 100);
        });
    }
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        document.body.appendChild(script);
    });
}

const RegisterForm = ({ onClose }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [errorMessageUserName, setErrorMessageUserName] = useState('');
    const [errorMessagePassword, setErrorMessagePassword] = useState('');
    const [showModal, setShowModal] = useState(false); // State to control modal visibility
    const [errorModal, setErrorModal] = useState(false); // State to control error modal visibility
    const [agreedToTerms, setAgreedToTerms] = useState(false); // New state for checkbox
    const [marketingOptIn, setMarketingOptIn] = useState(false); // Unticked by default — opt-in, not opt-out
    const [registeredUid, setRegisteredUid] = useState(null);
    const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
    const [loading, setLoading] = useState(false);
    const [buttonlabel, setButtonlabel] = useState('Processing...')
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileError, setTurnstileError] = useState('');
    const turnstileContainerRef = useRef(null);
    const turnstileWidgetIdRef = useRef(null);

    const { login } = useLogin();
    const { googleLogin } = useGoogleLogin();
    const [googleError, setGoogleError] = useState('');
    const setShowLogInWelcome = useAuthStore((state) => state.setShowLoginWelcomeModal);
    const setLoginWelcomeVariant = useAuthStore((state) => state.setLoginWelcomeVariant);


    useEffect(() => {
        if (isLoggedIn()) {
            onClose(); // Close the modal if already logged in
        }
    }, [isLoggedIn, onClose]);

    // Mount the Turnstile widget once, on this form instance.
    useEffect(() => {
        let cancelled = false;
        loadTurnstileScript().then(() => {
            if (cancelled || !turnstileContainerRef.current || turnstileWidgetIdRef.current) return;
            turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
                sitekey: TURNSTILE_SITE_KEY,
                callback: (token) => {
                    setTurnstileToken(token);
                    setTurnstileError('');
                },
                'expired-callback': () => setTurnstileToken(''),
                'error-callback': () => {
                    setTurnstileToken('');
                    setTurnstileError('Verification failed to load. Please refresh and try again.');
                },
            });
        });
        return () => {
            cancelled = true;
            if (turnstileWidgetIdRef.current && window.turnstile) {
                window.turnstile.remove(turnstileWidgetIdRef.current);
                turnstileWidgetIdRef.current = null;
            }
        };
    }, []);

    // While the "check your email" modal is up, poll for the user clicking
    // the activation link (possibly in a different tab/app) and auto-close
    // both this modal and the form modal behind it so they don't sit open
    // and confuse the applicant after they've already moved on.
    useEffect(() => {
        if (!showModal || !registeredUid) return;

        const interval = setInterval(async () => {
            try {
                const { data } = await axios.get(`registration-status/${registeredUid}/`);
                if (data?.email_confirmed) {
                    clearInterval(interval);
                    handleCloseModal();
                }
            } catch (err) {
                // Ignore transient polling errors — just try again next tick.
            }
        }, REGISTRATION_STATUS_POLL_MS);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, registeredUid]);

    const resetForm = () => {
        setUsername('');
        setPassword('');
        setPassword2('');
        setErrorMessageUserName(''); // Reset the error message
        setErrorMessagePassword(''); // Reset the error message
    };

    const handleRegister = async (e) => {
        e.preventDefault();

        if (!turnstileToken) {
            setTurnstileError('Please complete the verification check.');
            return;
        }

        setLoading(true);
        const response = await register(username, password, password2, turnstileToken, marketingOptIn);
        setLoading(false);

        // Turnstile tokens are single-use — Cloudflare consumes it on the
        // siteverify call regardless of whether registration as a whole
        // succeeded, so a fresh token is needed for any retry.
        if (turnstileWidgetIdRef.current && window.turnstile) {
            window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        setTurnstileToken('');

        if (response?.error) {
            const { error } = response;
            if (error.username) {
                setErrorMessageUserName(error.username);
            } else if (error.password) {
                setErrorMessagePassword(error.password);
            } else if (error.turnstile_token) {
                setTurnstileError(error.turnstile_token);
            } else {
                // Handle any other errors
                resetForm();
                setErrorModal(true); // Show the error modal
            }
        } else {
            // Successful registration, show modal and inform user to check email
            resetForm();
            setRegisteredUid(response?.data?.uid ?? null);
            setShowModal(true); // Show the success modal
        }
    };

    // Google sign-in creates (or auto-links) the account and logs the user
    // in immediately — no "check your email" step, since Google has
    // already verified the address. So this just closes the form modal,
    // same success path as a password login.
    const handleGoogleSuccess = async (credentialResponse) => {
        const { data, error } = await googleLogin(credentialResponse.credential);
        if (error) {
            setGoogleError(error.detail);
        } else {
            setLoginWelcomeVariant(data?.is_new_user ? 'new_google_account' : 'standard');
            setShowLogInWelcome(true);
            onClose();
        }
    };

    // Close the success modal
    const handleCloseModal = () => {
        setShowModal(false);
        onClose(); // Close the form modal after closing the success modal
    };

    // Close the error modal
    const handleCloseErrorModal = () => {
        setErrorModal(false);
    };

    return (
        <>
            <section>
                <GoogleLogin
                    text="signup_with"
                    onSuccess={handleGoogleSuccess}
                    onError={() => setGoogleError('Google sign-in failed. Please try again.')}
                />
                {googleError && (
                    <div style={{ color: 'red', marginTop: '10px' }}>
                        {googleError}
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', margin: '18px 0' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#adb5bd' }} />
                    <span style={{ padding: '0 10px', fontSize: '0.85rem', color: '#6c757d' }}>or register with email</span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#adb5bd' }} />
                </div>

                <Form onSubmit={handleRegister}>
                    <Form.Group>
                        <Form.Label htmlFor="username" className="small">Email (will be your username):</Form.Label>
                        <Form.Control 
                            type="text"
                            id="username"
                            name="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="password" className="small">Password:</Form.Label>
                        <Form.Control
                            type="password"
                            id="password"
                            name="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}                
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="confirmPassword" className="small">Confirm Password:</Form.Label>
                        <Form.Control
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={password2}
                            onChange={(e) => setPassword2(e.target.value)}                
                        />
                    </Form.Group>
                    {errorMessageUserName && (
                        <div style={{ color: 'red', marginTop: '10px' }}>
                            {errorMessageUserName}
                        </div>
                    )}
                    {errorMessagePassword && (
                        <div style={{ color: 'red', marginTop: '10px' }}>
                            {errorMessagePassword}
                        </div>
                    )}
                    
                    {/* Checkbox for agreeing to terms and conditions */}
                    <Form.Group controlId="termsCheckbox" className="mt-3">
                        <Form.Check
                            type="checkbox"
                            label="I have read and agree to the Terms of Service."
                            checked={agreedToTerms}
                            onChange={() => setAgreedToTerms(!agreedToTerms)}
                        />
                    </Form.Group>

                    {/* Opt-in (unticked by default) marketing email consent */}
                    <Form.Group controlId="marketingOptInCheckbox" className="mt-2">
                        <Form.Check
                            type="checkbox"
                            label="Send me occasional emails about the service, new features, updates and other relevant information"
                            checked={marketingOptIn}
                            onChange={() => setMarketingOptIn(!marketingOptIn)}
                        />
                    </Form.Group>

                    <div ref={turnstileContainerRef} style={{ marginTop: '12px' }}></div>
                    {turnstileError && (
                        <div style={{ color: 'red', marginTop: '10px' }}>
                            {turnstileError}
                        </div>
                    )}

                    <br />
                    {loading ? (
                        <ButtonExample data={buttonlabel} />
                    ) : (
                        <Button
                            variant="success"
                            type="submit"
                            disabled={!agreedToTerms} // Disable button if not agreed to terms
                        >
                            Register
                        </Button>
                    )}
                </Form>
            </section>

            {/* Modal to show after successful registration */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Registration Next Step</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    A verification link has been sent to your email. Please check your inbox to activate your account.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={handleCloseModal}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Error modal to show in case of unhandled error */}
            <Modal show={errorModal} onHide={handleCloseErrorModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Something went wrong. Contact administrator if issue persists.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseErrorModal}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RegisterForm;




// import React, { useEffect, useState } from 'react';
// import Button from 'react-bootstrap/Button';
// import Form from 'react-bootstrap/Form';
// import Modal from 'react-bootstrap/Modal'; // Import Modal from React-Bootstrap
// import { register } from '../utils/auth';
// import { useAuthStore } from '../store/auth';
// import useLogin from '../utils/login';
// import ButtonExample from "../components/loadingbutton.js";

// const RegisterForm = ({ onClose }) => {
//     const [username, setUsername] = useState('');
//     const [password, setPassword] = useState('');
//     const [password2, setPassword2] = useState('');
//     const [errorMessageUserName, setErrorMessageUserName] = useState(''); 
//     const [errorMessagePassword, setErrorMessagePassword] = useState(''); 
//     const [showModal, setShowModal] = useState(false); // State to control modal visibility
//     const [agreedToTerms, setAgreedToTerms] = useState(false); // New state for checkbox
//     const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
//     const [loading, setLoading] = useState(false);
//     const [buttonlabel, setButtonlabel] = useState('Processing...')

//     const { login } = useLogin();

//     const permittedUsers = ['toby.pittaway@gmail.com', 'toby.pittaway@googlemail.com', 'toby.pittaway@icloud.com'];

//     useEffect(() => {
//         if (isLoggedIn()) {
//             onClose(); // Close the modal if already logged in
//         }
//     }, [isLoggedIn, onClose]);

//     const resetForm = () => {
//         setUsername('');
//         setPassword('');
//         setPassword2('');
//         setErrorMessageUserName(''); // Reset the error message
//         setErrorMessagePassword(''); // Reset the error message
//     };

//     const handleRegister = async (e) => {
//         e.preventDefault();
//         // resetForm(); // Clear all error messages before proceeding
//         if (permittedUsers.includes(username)) {

//             setLoading(true);
//             const response = await register(username, password, password2);
//             setLoading(false);

//             if (response?.error) {
//                 const { error } = response;
//                 if (error.username) {
//                     setErrorMessageUserName(error.username);
//                 } else if (error.password) {
//                     setErrorMessagePassword(error.password);
//                 }
//             } else {
//                 // Successful registration, show modal and inform user to check email
//                 resetForm();
//                 setShowModal(true); // Show the success modal
//             }
//         } else {
//             setErrorMessageUserName('Contact administrator for access.');
//         }
//     };

//     // Close the modal and the parent form
//     const handleCloseModal = () => {
//         setShowModal(false);
//         onClose(); // Close the form modal after closing the success modal
//     };

//     return (
//         <>
//             <section>
//                 <Form onSubmit={handleRegister}>
//                     <Form.Group>
//                         <Form.Label htmlFor="username" className="small">Email (will be your username):</Form.Label>
//                         <Form.Control 
//                             type="text"
//                             id="username"
//                             name="username"
//                             value={username}
//                             onChange={(e) => setUsername(e.target.value)}
//                         />
//                     </Form.Group>
//                     <Form.Group>
//                         <Form.Label htmlFor="password" className="small">Password:</Form.Label>
//                         <Form.Control
//                             type="password"
//                             id="password"
//                             name="password"
//                             value={password}
//                             onChange={(e) => setPassword(e.target.value)}                
//                         />
//                     </Form.Group>
//                     <Form.Group>
//                         <Form.Label htmlFor="confirmPassword" className="small">Confirm Password:</Form.Label>
//                         <Form.Control
//                             type="password"
//                             id="confirmPassword"
//                             name="confirmPassword"
//                             value={password2}
//                             onChange={(e) => setPassword2(e.target.value)}                
//                         />
//                     </Form.Group>
//                     {errorMessageUserName && (
//                         <div style={{ color: 'red', marginTop: '10px' }}>
//                             {errorMessageUserName}
//                         </div>
//                     )}
//                     {errorMessagePassword && (
//                         <div style={{ color: 'red', marginTop: '10px' }}>
//                             {errorMessagePassword}
//                         </div>
//                     )}
                    
//                     {/* Checkbox for agreeing to terms and conditions */}
//                     <Form.Group controlId="termsCheckbox" className="mt-3">
//                         <Form.Check 
//                             type="checkbox"
//                             label="I have read and agree to the Terms of Service."
//                             checked={agreedToTerms}
//                             onChange={() => setAgreedToTerms(!agreedToTerms)}
//                         />
//                     </Form.Group>

//                     <br />
//                     {loading ? (
//                         <ButtonExample data={buttonlabel} />
//                     ) : (
//                         <Button 
//                             variant="success" 
//                             type="submit" 
//                             disabled={!agreedToTerms} // Disable button if not agreed to terms
//                             >
//                             Register
//                         </Button>
//                     )}
//                 </Form>
//             </section>

//             {/* Modal to show after successful registration */}
//             <Modal show={showModal} onHide={handleCloseModal}>
//                 <Modal.Header closeButton>
//                     <Modal.Title>Registration Successful</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body>
//                     A verification link has been sent to your email. Please check your inbox to activate your account.
//                 </Modal.Body>
//                 <Modal.Footer>
//                     <Button variant="success" onClick={handleCloseModal}>
//                         OK
//                     </Button>
//                 </Modal.Footer>
//             </Modal>
//         </>
//     );
// };

// export default RegisterForm;
